import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore, useIsAuthenticated, useNotificationStore } from '@soundbyte/stores';
import { generationService } from '@soundbyte/services';
import { Header, Sidebar } from '@/components/shared';
import { ToastContainer } from '@/components/ui';
import { PageSpinner } from '@/components/ui/spinner';
import { ROUTES } from '@soundbyte/config/constants';
import { AlertTriangle } from 'lucide-react';


// Session check interval (5 minutes)
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000;

// Routes that should be blocked during maintenance mode
const MAINTENANCE_BLOCKED_ROUTES = ['/sonic', '/originals', '/create'];

export const DashboardLayout: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const checkSession = useAuthStore((state) => state.checkSession);
  const fetchUnreadCount = useNotificationStore((state) => state.fetchUnreadCount);
  const location = useLocation();

  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState<string | null>(null);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  // NOTE: Auth redirection is handled declaratively by `<Navigate>` below.
  // A previous version also did `useEffect(() => navigate(LOGIN))` which
  // could fire alongside the `<Navigate>` and race on auth flips. Removed.

  // Periodically check if the session is still valid. Skip while the tab
  // is hidden so a sleeping laptop doesn't burst requests on wake.
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSessionValidity = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        await checkSession();
      } catch {
        // Session check failed - user will be redirected by auth state change
      }
    };

    // Check session immediately and then every 5 minutes
    checkSessionValidity();
    const intervalId = setInterval(checkSessionValidity, SESSION_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, checkSession]);

  // Fetch notification count on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
    }
  }, [isAuthenticated, fetchUnreadCount]);

  // Check maintenance status. Skip pre-auth since the endpoint may require
  // a session and would 401 in a loop otherwise.
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkMaintenanceStatus = async () => {
      try {
        const response = await generationService.getPlaygroundStatus();
        if (response.success && response.data) {
          setMaintenanceMode(response.data.maintenanceMode);
          setMaintenanceMessage(response.data.maintenanceMessage);
        }
      } catch {
        // On error, assume not in maintenance mode
      } finally {
        setMaintenanceChecked(true);
      }
    };

    checkMaintenanceStatus();
  }, [isAuthenticated]);

  // Show loading while checking auth (skip if bypassing)
  if (!isInitialized) {
    return <PageSpinner />;
  }

  // Redirect to login if not authenticated (skip if bypassing)
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Check if current route should be blocked during maintenance
  const isBlockedRoute = MAINTENANCE_BLOCKED_ROUTES.some(route =>
    location.pathname.startsWith(route)
  );

  // Show maintenance screen for blocked routes
  if (maintenanceChecked && maintenanceMode && isBlockedRoute) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 min-h-[calc(100vh-4rem)] p-4 lg:p-6">
            <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center">
              <div className="p-4 rounded-full bg-warning/10 mb-6">
                <AlertTriangle className="h-12 w-12 text-warning" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-4">
                Playground Under Maintenance
              </h1>
              <p className="text-foreground/90 max-w-md mb-6">
                {maintenanceMessage || 'The playground is currently under maintenance. Please try again later.'}
              </p>
              <p className="text-foreground/90 text-sm">
                You can still view your dashboard and previous creations while we work on improvements.
              </p>
            </div>
          </main>
        </div>
        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />

      {/* Main content area */}
      <div className="flex">
        {/* Sidebar */}
        <Sidebar />

        {/* Page content */}
        <main className="flex-1 min-h-[calc(100vh-4rem)] p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
};
